import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Volume2, VolumeX, Music } from "lucide-react";
import { getSoundSettings, setSoundSettings, playElegantClick } from "@/lib/sound";

export default function SoundSettings() {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(true);
  const [volume, setVolume] = useState(0.45);

  useEffect(() => {
    const s = getSoundSettings();
    setEnabled(s.enabled);
    setVolume(s.volume);
  }, []);

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    setSoundSettings({ enabled: next });
    if (next) playElegantClick();
  };

  const onVolume = (v) => {
    setVolume(v);
    setSoundSettings({ volume: v });
  };

  return (
    <section
      className="p-6 mb-6"
      style={{ background: "var(--bg-surface)", border: "1px solid var(--border-accent)" }}
      data-testid="settings-sound"
    >
      <div className="flex items-start gap-4 mb-5">
        <Music size={20} strokeWidth={1.4} color="var(--brand-primary)" />
        <div className="flex-1">
          <h2 className="font-serif text-2xl mb-1" style={{ color: "var(--text-primary)" }}>
            {t("settings.sound_title")}
          </h2>
          <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
            {t("settings.sound_desc")}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={toggle}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] px-4 py-2"
          style={{
            border: `1px solid ${enabled ? "var(--brand-primary)" : "var(--border-subtle)"}`,
            background: enabled ? "rgba(255,0,51,0.08)" : "var(--bg-elevated)",
            color: enabled ? "var(--brand-primary)" : "var(--text-secondary)",
          }}
          data-testid="sound-toggle"
        >
          {enabled ? <Volume2 size={14} /> : <VolumeX size={14} />} {enabled ? t("settings.sound_on") : t("settings.sound_off")}
        </button>

        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(volume * 100)}
          onChange={(e) => onVolume(parseInt(e.target.value, 10) / 100)}
          onMouseUp={() => enabled && playElegantClick()}
          className="flex-1 accent-[var(--brand-primary)]"
          disabled={!enabled}
          data-testid="sound-volume"
          aria-label="Volumen"
        />
        <span className="text-xs tabular-nums w-10 text-right" style={{ color: "var(--text-muted)" }}>
          {Math.round(volume * 100)}%
        </span>

        <button
          type="button"
          onClick={() => enabled && playElegantClick()}
          className="btn-ghost text-xs"
          disabled={!enabled}
          data-testid="sound-test"
        >
          {t("settings.sound_test")}
        </button>
      </div>
    </section>
  );
}
